import { useState, useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import { Default } from "../../../components/Default/Default.jsx";

import { ListItem, SectionHeader, LoadingContent } from "../../../components/SharedComponents/sharedComponents.index.jsx";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants.js";

import { MainIndexes } from "./components/MainIndexes/MainIndexes.jsx";
import { FiltersAndSums } from "./components/FiltersAndSums/FiltersAndSums.jsx"

import { i18n } from "../../../translate/i18n.js";

import { useProductStore } from "../../../viewModel/stores/productStore.js";
import { useAnalysisViewModel } from "../../../viewModel/useAnalysysViewModel.js";
import { useOtbViewModel } from "../../../viewModel/useOtbVewModel.js";

import { useForecastStore } from "../../../viewModel/stores/forecastStore.js";

import { getNextLevel } from "../../../utils/utils.js";
import { OPPORTUNITY_STATUS } from "../../../components/components.constants.js";

export function OTBAdjustments() {

  const { latestForecast } = useForecastStore(
    useShallow((state) => ({ latestForecast: state.latestForecast }))
  );

  const { predictions, selectedMonthsValue, originalMonthColumns } = latestForecast || {};

  const [selectedValue, setSelectedValue] = useState(1);
  const [selectedMonthsValueToDisplay, setSelectedMonthsValueToDisplay] = useState(selectedMonthsValue)

  const { t } = i18n;

  const vm = useOtbViewModel({ shouldFetchActiveOpp: OPPORTUNITY_STATUS.ACTIVE });
  const vmAnalysis = useAnalysisViewModel({ 
    shouldFetchProducts: true,
    shouldFetchForecastData: true,
    shouldFetchFiltersAndDriversOptions: true
  });

  const [selectedLevels, setSelectedLevels] = useState({});

  const productViewRef = useRef(null);

  const scrollToProductView = () => {
    if (productViewRef.current) {
      productViewRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const defineProductFilter = (filterBy) => {
    scrollToProductView();
    setSelectedLevels(filterBy);
  }

  const groupFiltersAndDriversOptions = useProductStore((s) => s.groupFiltersAndDriversOptions);

  const selected = useMemo(() => {
    return !groupFiltersAndDriversOptions.length ? null : groupFiltersAndDriversOptions.find(option => option.value === selectedValue);
  }, [groupFiltersAndDriversOptions, selectedValue]);

  const filteredMonthColumns = useMemo(() => {
    return !selectedMonthsValueToDisplay?.length || !originalMonthColumns?.length
      ? originalMonthColumns || []
      : originalMonthColumns.filter(column => selectedMonthsValueToDisplay.includes(column.predictionId));
  }, [selectedMonthsValueToDisplay, originalMonthColumns]);  

  const currentFilteredPredictions = useMemo(() => {
    if (!selected?.productIds?.length) return [];
    if (!selectedMonthsValueToDisplay?.length) return [];

    const selectedMonthsArray = filteredMonthColumns.map(month => month.month.toString() + month.year.toString());

    return predictions.filter(prediction => {
      const predictionDate = prediction.month.toString() + prediction.year.toString();
      const matchesProduct = selected.productIds.includes(prediction.productId);
      const matchesMonth = selectedMonthsArray.includes(predictionDate);

      return matchesProduct && matchesMonth;
    });
  }, [selected, selectedMonthsValueToDisplay, filteredMonthColumns, predictions]);

  const calculatePredictionSummary = (field1, field2, items = []) => {

    const sumByField = (items, field) =>
      items.reduce((acc, item) => {
        const value = item[field];
        return value != null ? acc + Number(value) : acc;
    }, 0);

    const clientPrediction = sumByField(items, field1);
    const aidronPrediction = sumByField(items, field2);

    return {
      clientPrediction,
      aidronPrediction,
      difference: clientPrediction - aidronPrediction
    };
  }; 

  const hierarchyGroups = useMemo(() => {
    if (!selected?.level || !vmAnalysis?.ctx?.products) return [];

    const currentLevelNumber = parseInt(selected.level.replace('level', ''));
    const nextLevel = `level${currentLevelNumber + 1}`;

    let levelGroups = getNextLevel(selected, nextLevel, vmAnalysis?.ctx?.products) || [];
    
    if (!levelGroups[0]?.name) {
      levelGroups = getNextLevel(selected, selected.level, vmAnalysis?.ctx?.products);
    }

    if (!levelGroups || levelGroups.length === 0 || !levelGroups[0]?.name) {
      const groupName = selected?.groupDisplayName || "";
      
      levelGroups = [{
        name: groupName,
        productIds: selected.productIds,
        level: selected.level,
        isCurrentLevel: true
      }];
    }

    return levelGroups;
  }, [selected, vmAnalysis?.ctx?.products]);

  const groupsWithIndexes = useMemo(() => {
    if (!hierarchyGroups.length || !currentFilteredPredictions.length) return [];

    return hierarchyGroups.map(group => {
      const groupPredictions = currentFilteredPredictions.filter(prediction =>
        group.productIds.includes(prediction.productId)
      );

      const groupSummary = calculatePredictionSummary("clientPrediction", "aidronPrediction", groupPredictions);

      return {
        ...group,
        ...groupSummary
      };
    });
  }, [hierarchyGroups, currentFilteredPredictions]);  

  const opportunitySummary = useMemo(() => {
    if (!groupsWithIndexes.length) {
      return { overstock: 0, increaseInSales: 0 };
    }

    return groupsWithIndexes.reduce(
      (acc, group) => {
        const difference = group.difference || 0;
        
        if (difference > 0) {
          acc.overstock += difference;
        }

        if (difference < 0) {
          acc.increaseInSales += difference;
        }
        
        return acc;
      },
      { overstock: 0, increaseInSales: 0 }
    );
  }, [groupsWithIndexes]);

  const totalSummary = useMemo(
    () => calculatePredictionSummary("clientPrediction", "aidronPrediction", currentFilteredPredictions),
    [currentFilteredPredictions]
  );

  if (vm.uiState.isLoadingProducts || !predictions || !originalMonthColumns) {
    return (
      <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA} mode={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>
    )
  }

  return (
    <Default>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          inlineSize: "100%",
          padding: "2rem 0 0 1rem",
          gap: '2rem'
        }}>

        <SectionHeader 
          ctx={{ 
            heading:  t("otbPageTitle"), 
            subtitles: [`${t("forecastGeneratedIn")} ${vmAnalysis.ctx.generationDate || "01/01/1970"}`],
            pageHeading: true
          }}
          uiState={{ headingSize: "lg" }}/>

          <FiltersAndSums 
            actions={{ setSelectedMonthsValue: setSelectedMonthsValueToDisplay,setSelectedValue: setSelectedValue }}
            ctx={{ 
              originalMonthColumns: originalMonthColumns,
              selected: selected,
              groupInformationData: groupFiltersAndDriversOptions,
              opportunitySummary: opportunitySummary
             }}
            uiState={{ selectedMonthsValue: selectedMonthsValue }}/>

          <MainIndexes
            ctx={{ 
              totalSummary: totalSummary, 
              groupsWithIndexes: groupsWithIndexes
            }}
            uiState={{
              selected: selected,
              defineProductFilter: defineProductFilter,
              selectedLevels: selectedLevels
            }}/>  

          <section style={{ display: 'grid', gridTemplateRows: 'auto', gap: '10px' }}>
            {vm.uiState.sortedActiveOpportunities.map((opportunity) => (
              <ListItem 
                ctx={{
                  opportunity: opportunity,
                  activeOppsWithTypes: vm.ctx.activeOpportunities?.activeOpportunities,
                  predictions: predictions,
                  products: vmAnalysis?.ctx?.products,
                }}
                uiState={{
                  reactivateOpportunity: true,
                  accordionButton: true,
                  accordionMode: "advancedOpportunities"
                }}
                key={opportunity.opportunityId} 
              />
            ))}        
          </section>          

      </div>

    </Default>
  );
}